// Unified article-text helpers used by BOTH the article detail page and the
// «آخر الأخبار» widget. Keeping the logic in a single module guarantees the
// two surfaces always render the exact same body text and can never disagree.

// Strip HTML while preserving paragraph breaks. Never truncates.
export function stripHtmlPreserveBreaks(html) {
    if (!html) return "";
    const s = String(html);
    if (!/<[a-z][\s\S]*?>/i.test(s)) return s.replace(/\r\n?/g, "\n").trim();
    const tmp = document.createElement("div");
    tmp.innerHTML = s
        .replace(/<\/(p|div|li|h[1-6]|blockquote|tr)>/gi, "\n")
        .replace(/<br\s*\/?>/gi, "\n");
    return (tmp.textContent || tmp.innerText || "")
        .replace(/\r\n?/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}

// Normalise a text for comparison purposes only (never rendered).
export const normText = (s) => (s || "").replace(/\s+/g, " ").trim();

// Invisible/zero-width characters (variation selector, ZWSP/J/NJ, word joiner,
// BOM) that leak into text from Telegram, emoji flags, or copy-paste. They
// distort layout & meta tags. This cleaner is safe: it never touches Arabic
// letters, digits, or punctuation — only the invisible code points below.
const INVISIBLE_RE = /[\u200B\u200C\u200D\u2060\uFEFF\uFE0F\u180E\u00AD]/g;

export function stripInvisible(s) {
    if (!s) return "";
    return String(s).replace(INVISIBLE_RE, "");
}

// Central visitor-facing sanitiser: strip HTML for meta tags / cards, and
// remove invisible glyphs everywhere.
export function cleanDisplayText(s) {
    return stripInvisible(stripHtmlPreserveBreaks(s)).trim();
}

// Detect real HTML content (has at least one tag).
const looksLikeHtml = (s) => /<[a-z][\s\S]*?>/i.test(s || "");

// Choose the ONE body value to render on both surfaces.
// Rules (priority order):
//   1. If content and excerpt exist, pick the one that CONTAINS the other
//      (keeps the fullest information, drops the redundant shorter twin).
//   2. If neither contains the other, prefer content (the main body field).
//   3. If only one exists, use that.
//   4. If both are empty, fall back to title.
// Returns { text: string, isHtml: boolean } — the `isHtml` flag reflects
// whether the chosen value actually contains HTML tags.
export function pickBody(article) {
    if (!article) return { text: "", isHtml: false };
    const content = String(article.content || "");
    const excerpt = String(article.excerpt || "");
    const cPlain = normText(stripHtmlPreserveBreaks(content));
    const ePlain = normText(excerpt);

    if (cPlain && ePlain) {
        if (ePlain === cPlain || cPlain.includes(ePlain)) return { text: content, isHtml: looksLikeHtml(content) };
        if (ePlain.includes(cPlain)) return { text: excerpt, isHtml: false };
        // Different information → keep the main body (content).
        return { text: content, isHtml: looksLikeHtml(content) };
    }
    if (cPlain) return { text: content, isHtml: looksLikeHtml(content) };
    if (ePlain) return { text: excerpt, isHtml: false };
    return { text: String(article.title || "").trim(), isHtml: false };
}

// If the body starts with the title (verbatim, after normalisation), strip
// that leading segment so we never render the title twice. Handles both:
//   (a) leading paragraphs whose combined text equals the title.
//   (b) a single paragraph whose beginning matches the title.
export function stripLeadingTitle(text, title, isHtml) {
    if (!text || !title) return text;
    const t = normText(title);
    if (!t) return text;
    const plainText = isHtml ? stripHtmlPreserveBreaks(text) : text;
    const p = normText(plainText);
    if (!p.startsWith(t)) return text;
    if (isHtml) {
        const tmp = document.createElement("div");
        tmp.innerHTML = text;
        // Path 0: a bare leading TEXT node (legacy Telegram bodies: "title\n\n<img …>").
        // Exact match after whitespace normalisation only — never fuzzy.
        while (tmp.firstChild && tmp.firstChild.nodeType === 3 && !normText(tmp.firstChild.textContent)) tmp.removeChild(tmp.firstChild);
        if (tmp.firstChild && tmp.firstChild.nodeType === 3) {
            const raw = tmp.firstChild.textContent || "";
            const n = normText(raw);
            if (n === t) { tmp.removeChild(tmp.firstChild); return tmp.innerHTML.trim(); }
            if (n.startsWith(t + " ")) {
                let titleIdx = 0, cutAt = 0;
                for (let i = 0; i < raw.length && titleIdx < title.length; i++) {
                    const rc = raw[i], tc = title[titleIdx];
                    if (rc === tc) { titleIdx++; cutAt = i + 1; continue; }
                    if (/\s/.test(rc)) { cutAt = i + 1; continue; }
                    if (/\s/.test(tc)) { titleIdx++; continue; }
                    break;
                }
                if (titleIdx >= title.length && /^[\s\-–—:،.؛]*(\n|$)/.test(raw.slice(cutAt))) {
                    while (cutAt < raw.length && /[\s\-–—:،.؛]/.test(raw[cutAt])) cutAt++;
                    tmp.firstChild.textContent = raw.slice(cutAt);
                    return tmp.innerHTML.trim();
                }
            }
        }
        // Path A: peel whole leading elements that match the title exactly.
        let acc = "";
        while (tmp.firstElementChild) {
            const seg = normText((tmp.firstElementChild.textContent || ""));
            const nextAcc = normText(acc + " " + seg).trim();
            if (t === nextAcc) {
                tmp.removeChild(tmp.firstElementChild);
                acc = nextAcc;
                break;
            }
            if (t.startsWith(nextAcc + " ")) {
                acc = nextAcc;
                tmp.removeChild(tmp.firstElementChild);
                continue;
            }
            break;
        }
        // Path B: if the very first remaining element STARTS with the leftover
        // title fragment, strip that fragment from its text content only.
        const remaining = tmp.firstElementChild;
        if (remaining && !acc) {
            const rawText = remaining.textContent || "";
            const rawNorm = normText(rawText);
            if (rawNorm.startsWith(t)) {
                // Locate the title within the raw string (accounting for whitespace).
                // Walk char by char, skipping whitespace, until we've matched `t`.
                let titleIdx = 0, cutAt = 0;
                for (let i = 0; i < rawText.length && titleIdx < title.length; i++) {
                    const rc = rawText[i];
                    const tc = title[titleIdx];
                    if (rc === tc) { titleIdx++; cutAt = i + 1; continue; }
                    if (/\s/.test(rc)) { cutAt = i + 1; continue; }
                    if (/\s/.test(tc)) { titleIdx++; continue; }
                    break;
                }
                if (titleIdx >= title.length && /^[\s\-–—:،.؛]*(\n|$)/.test(rawText.slice(cutAt))) {
                    // Skip trailing separators like ".", "،", ":" etc.
                    while (cutAt < rawText.length && /[\s\-–—:،.؛]/.test(rawText[cutAt])) cutAt++;
                    remaining.textContent = rawText.slice(cutAt);
                }
            }
        }
        return tmp.innerHTML.trim();
    }
    // Plain-text path: strip the title only when it occupies its own line(s). A title that is merely the
    // opening words of the first sentence stays — cutting it would leave a sentence fragment.
    const idx = plainText.toLowerCase().indexOf(title.toLowerCase());
    if (idx === 0) {
        const rest = plainText.slice(title.length);
        if (/^[\s\-–—:،.؛]*(\n|$)/.test(rest)) return rest.replace(/^[\s\-–—:،.؛]+/, "").trim();
    }
    return plainText;
}

// Widget-specific: NEVER strips the title from the body. The list must
// display each story from its first sentence to its last, so we combine
// title + content when they carry DIFFERENT information (typical of legacy
// Telegram articles where the title held the first sentence and content
// held the rest).
//
// Rules (in order):
//   1. content empty → fall back to excerpt → title.
//   2. content already contains the title → use content alone (fresh format).
//   3. title fully contains content → use title alone.
//   4. Otherwise → prepend the title so both pieces of information appear
//      exactly once, separated by a paragraph break.
export function getLatestFullText(article) {
    if (!article) return "";
    const rawTitle = String(article.title || "").trim();
    const contentHtml = String(article.content || "");
    const rawExcerpt = String(article.excerpt || "").trim();

    const contentPlain = stripHtmlPreserveBreaks(contentHtml);
    const titleN = normText(rawTitle);
    const contentN = normText(contentPlain);

    if (!contentN) {
        if (rawExcerpt) return rawExcerpt;
        // Last resort — the title with any trailing colon dropped so we
        // don't display a dangling stub like "القناة 13:".
        return rawTitle.replace(/[:：]\s*$/, "").trim();
    }
    if (!titleN) return contentPlain;

    if (contentN.includes(titleN)) return contentPlain;
    if (titleN.includes(contentN)) return rawTitle;

    // Different information → title first, then the rest, separated by a
    // paragraph break so `white-space: pre-wrap` renders them cleanly.
    return `${rawTitle}\n\n${contentPlain}`;
}

